import { useState, useEffect, useMemo } from "react";
import { PulseLoader } from "react-spinners";
import { loadStripe } from "@stripe/stripe-js";
import { useTranslation } from "react-i18next";
import {
  PaymentElement,
  Elements,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { CreatePaymentIntent } from "./Requests";
import { formatMinor } from "../Components/Reusable/CurrencyFormatter/CurrencyFormatter";

/** Map your i18n codes to Stripe supported locales */
const STRIPE_LOCALE_MAP = {
  auto: "auto",
  en: "en",
  "en-GB": "en-GB",
  fr: "fr",
  "fr-CA": "fr-CA",
  de: "de",
  es: "es",
  "es-419": "es-419",
  it: "it",
  nl: "nl",
  pt: "pt",
  "pt-BR": "pt-BR",
  sv: "sv",
  da: "da",
  nb: "nb",
  fi: "fi",
  pl: "pl",
  cs: "cs",
  sk: "sk",
  sl: "sl",
  hu: "hu",
  ro: "ro",
  bg: "bg",
  el: "el",
  lt: "lt",
  lv: "lv",
  et: "et",
  tr: "tr",
  ru: "ru",
  ja: "ja",
  ko: "ko",
  th: "th",
  id: "id",
  vi: "vi",
  ms: "ms",
  mt: "mt",
  zh: "zh",
  "zh-HK": "zh-HK",
  "zh-TW": "zh-TW",
  ar: "ar"
};

function normalizeStripeLocale(lang) {
  if (!lang) return "auto";
  const exact = STRIPE_LOCALE_MAP[lang];
  if (exact) return exact;
  const lower = lang.toLowerCase();
  return STRIPE_LOCALE_MAP[lower] || STRIPE_LOCALE_MAP[lower.replace("_", "-")] || "auto";
}

const CheckoutForm = (props) => {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation("makeBooking");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // return_url: window.location.origin + "/payment-result",
      },
      redirect: "if_required",
    });

    if (error) {
      if (error.type !== "validation_error") {
        setMessage(error.message || "Payment failed");
        props.bookingResult("failed");
      }
    } else {
      setMessage("Payment succeeded!");
      props.bookingResult("success");
    }
    setIsLoading(false);
  };

  return (
    <form className="booking-portal-step-wrapper" aria-label={t("makeBooking:stepFive.titles.payment")}>
      <div className="payment-container">
        <div className="card-element-wrapper">
          <div aria-label={t("makeBooking:stepFive.labels.serviceWithStaffAria", { service: props.service.name, staff: props.barber.name })}>
            {t("makeBooking:stepFive.labels.serviceWithStaff", { service: props.service.name, staff: props.barber.name })}
          </div>
          <div aria-label={t("makeBooking:stepFive.labels.dateTimeAria", { date: new Date(props.date).toDateString(), time: props.time })}>
            {t("makeBooking:stepFive.labels.dateTime", { date: new Date(props.date).toDateString(), time: props.time })}
          </div>
          <div aria-label={t("makeBooking:stepFive.labels.amountAria", { amount: formatMinor(props.service.onlinePaymentAmount, props.currency) })}>
            {formatMinor(props.service.onlinePaymentAmount, props.currency)}
          </div>
        </div>
      </div>

      <div className="payment-container">
        <div className="card-form" role="form" aria-label={t("makeBooking:stepFive.titles.payment")}>
          <div className="card-element-wrapper">
            <PaymentElement />
          </div>

          <button
            onClick={handleSubmit}
            className="submit-button"
            disabled={isLoading || !stripe || !elements}
            aria-busy={isLoading ? "true" : "false"}
          >
            {isLoading
              ? t("makeBooking:stepFive.actions.processing")
              : t("makeBooking:stepFive.actions.payAmount", {
                  amount: formatMinor(props.service.onlinePaymentAmount, props.currency),
                })}
          </button>

          {message && <div role="alert">{message}</div>}
        </div>
      </div>
    </form>
  );
};

const StepFive = (props) => {
  const { i18n } = useTranslation();
  const [clientSecret, setClientSecret] = useState("");
  const [stripeAccountId, setStripeAccountId] = useState("");
  const [bookingResult, setBookingResult] = useState(false);

  useEffect(() => {
    CreatePaymentIntent(props.barber.barberId, props.service.serviceId, props.timeId, props.service.duration, props.firstName, props.lastName, props.email, props.mobile, props.date, props.time)
      .then((res) => {
        if (res.status === 200) {
          setClientSecret(res.data.clientSecret);
          setStripeAccountId(res.data.connectedAccountId);
        }
      })
      .catch(() => {});
  }, []);

  // Stripe instance (publishable key + connected account)
  const stripePromise = useMemo(
    () =>
      loadStripe(
        "pk_test_51SBJonHU5Q15k1yv4gnl0NOUPYK3RXGkEwamz2FDMDwyMdWCZIdmlQ913sEFslaBmsQEb2rWzhwT0rL5jvjfeFYJ00HCRd18ZL",
        { stripeAccount: stripeAccountId }
      ),
    [stripeAccountId]
  );

  // Locale for Stripe Elements
  const stripeLocale = useMemo(() => normalizeStripeLocale(i18n.language), [i18n.language]);

  // Elements options (locale lives here)
  const options = useMemo(
    () => ({
      clientSecret,
      locale: stripeLocale,
      // optional: customize look & feel
      // appearance: { theme: props.theme === 2 ? "flat" : "stripe" }
    }),
    [clientSecret, stripeLocale]
  );

  // bubble result up
  props.bookingResult(bookingResult);

  return clientSecret ? (
    // key forces remount when locale changes so Elements reinitializes in the new language
    <Elements stripe={stripePromise} options={options} key={`${clientSecret}:${stripeLocale}`}>
      <CheckoutForm
        currency={props.currency}
        theme={props.theme}
        bookingResult={setBookingResult}
        service={props.service}
        date={props.date}
        barber={props.barber}
        time={props.time}
      />
    </Elements>
  ) : (
    <div style={{ height: "100vh" }}>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <div style={{ marginTop: "2rem" }}>
          <PulseLoader color={"#333"} size={25} aria-label="Loading" data-testid="loader" />
        </div>
      </div>
    </div>
  );
};

export default StepFive;
